/**
 * SaveStatusIndicator
 *
 * Inline pill showing the background-save state of a single resource.
 *
 *   pending / saving  →  amber spinner "Saving…"
 *   retrying          →  amber spin    "Retrying…"
 *   saved             →  green check   "Saved"  (auto-fades after 4 s)
 *   failed            →  red warning   "Save failed" + "Retry" button
 *   absent / cleared  →  renders nothing
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { useResources, type SaveStatus } from './resources-context';

interface Props {
  /** The resource ID to track.  Pass null/undefined before a resource exists. */
  resourceId: string | null | undefined;
  className?: string;
}

const STATUS_LABEL: Record<SaveStatus, string> = {
  pending:  'Saving…',
  saving:   'Saving…',
  retrying: 'Retrying…',
  saved:    'Saved',
  failed:   'Save failed',
};

export function SaveStatusIndicator({ resourceId, className = '' }: Props) {
  const { saveStatuses, retrySave } = useResources();
  const status: SaveStatus | undefined =
    resourceId ? saveStatuses[resourceId] : undefined;

  return (
    <AnimatePresence mode="wait">
      {status && (
        <motion.span
          key={status}
          initial={{ opacity: 0, scale: 0.88, x: 6 }}
          animate={{ opacity: 1, scale: 1,    x: 0 }}
          exit={{    opacity: 0, scale: 0.88, x: 6 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className={`inline-flex items-center gap-1.5 ${className}`}
        >
          {/* ── Saving / pending ── */}
          {(status === 'pending' || status === 'saving') && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium select-none">
              <Loader2 size={12} className="animate-spin" />
              {STATUS_LABEL[status]}
            </span>
          )}

          {/* ── Retrying ── */}
          {status === 'retrying' && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium select-none">
              <RefreshCw size={12} className="animate-spin" />
              Retrying…
            </span>
          )}

          {/* ── Saved ✓ ── */}
          {status === 'saved' && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium select-none">
              <CheckCircle2 size={12} />
              Saved
            </span>
          )}

          {/* ── Failed ── */}
          {status === 'failed' && (
            <span className="inline-flex items-center gap-1.5 select-none">
              <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertCircle size={12} />
                Save failed
              </span>
              {resourceId && (
                <button
                  onClick={() => retrySave(resourceId)}
                  className="text-xs font-semibold text-red-600 underline underline-offset-2 hover:text-red-700 transition-colors"
                >
                  Retry
                </button>
              )}
            </span>
          )}
        </motion.span>
      )}
    </AnimatePresence>
  );
}
